import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader } from "@/components/common/Primitives";
import { DataTable } from "@/components/common/DataTable";
import { CustomerForm } from "@/components/forms/CustomerForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CUSTOMER_STATUS_OPTS } from "@/lib/constants";
import { Plus, Search, Building2, User } from "lucide-react";

export default function Customers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => { if (params.get("neu")) { setFormOpen(true); setParams({}); } }, [params, setParams]);

  const { data, isLoading } = useQuery({
    queryKey: ["customers", q, status],
    queryFn: async () => (await api.get(`/customers?q=${encodeURIComponent(q)}&status=${status}`)).data,
  });

  const columns = [
    { key: "name", header: "Kunde", render: (c) => (
      <div className="flex items-center gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
          {c.type === "business" ? <Building2 className="h-4 w-4" /> : <User className="h-4 w-4" />}
        </span>
        <div className="min-w-0"><div className="truncate font-medium">{c.display_name || "—"}</div>
          <div className="truncate text-xs text-muted-foreground">{c.customer_number}</div></div>
      </div>
    ) },
    { key: "contact", header: "Kontakt", mobileHidden: true, render: (c) => (
      <div className="text-xs"><div>{c.email || "—"}</div><div className="text-muted-foreground">{c.phone || c.mobile || ""}</div></div>
    ) },
    { key: "city", header: "Ort", render: (c) => `${c.postal_code} ${c.city}`.trim() || "—" },
    { key: "assigned_user_name", header: "Betreuer", mobileHidden: true, render: (c) => c.assigned_user_name || "—" },
    { key: "status", header: "Status", render: (c) => <Badge variant="secondary" className="capitalize">{c.status}</Badge> },
  ];

  return (
    <div>
      <PageHeader title="Kunden" description={`${data?.total ?? 0} Kunden im Kundenstamm`}
        actions={can(user?.role, "customers:write") && (
          <Button className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setFormOpen(true)} data-testid="new-customer-button">
            <Plus className="h-4 w-4" /> Neuer Kunde
          </Button>
        )} />

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, Nr., Telefon, E-Mail…" className="pl-9" data-testid="customer-search-input" />
        </div>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="sm:w-44" data-testid="customer-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            {CUSTOMER_STATUS_OPTS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <DataTable columns={columns} rows={data?.items} loading={isLoading}
        onRowClick={(c) => navigate(`/kunden/${c.id}`)} testIdPrefix="customer-row" />

      <CustomerForm open={formOpen} onOpenChange={setFormOpen}
        onSaved={() => qc.invalidateQueries({ queryKey: ["customers"] })} />
    </div>
  );
}
